import { useState, ChangeEvent } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUpdate, useRecordContext, useNotify } from "react-admin";
import { useCanAccess } from '@react-admin/ra-rbac';

function SecurityTab() {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const user = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPasswords({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handlePasswordChange =
    (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
      setPasswords((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSubmitPasswordChange = () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      notify(t('Passwords do not match'), { type: 'error' });
      return;
    }
    
    update(
      "userPassword",
      { 
        id: user.id, 
        data: { 
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword 
        } 
      },
      {
        onSuccess: () => {
          notify(t('Password updated'));
          handleCloseDialog();
        },
        onError: (error) => {
          notify(t('The password could not be updated'), { type: 'error' });
        },
      }
    );
  };

  const { canAccess } = useCanAccess({
    action: 'edit',
    resource: 'users',
  });

  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">{t("Security")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Password used to sign in to the admin.")}
          </Typography>
        </Box>
      )}
      <Card>
        <Box p={2.5} display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {t("Password")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("The current password is required to set a new one.")}
            </Typography>
          </Box>
          <Button variant="outlined" onClick={handleOpenDialog} disabled={!canAccess}>
            {t("Change password")}
          </Button>
        </Box>
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("Change password")}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              type="password"
              label={t("Current password")}
              value={passwords.currentPassword}
              onChange={handlePasswordChange("currentPassword")}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label={t("New password")}
              value={passwords.newPassword}
              onChange={handlePasswordChange("newPassword")}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label={t("Confirm new password")}
              value={passwords.confirmPassword}
              onChange={handlePasswordChange("confirmPassword")}
              margin="normal"
              error={
                passwords.newPassword !== passwords.confirmPassword &&
                passwords.confirmPassword !== ""
              }
              helperText={
                passwords.newPassword !== passwords.confirmPassword &&
                passwords.confirmPassword !== ""
                  ? t("Passwords do not match")
                  : ""
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSubmitPasswordChange}
            disabled={
              !passwords.currentPassword ||
              !passwords.newPassword ||
              !passwords.confirmPassword ||
              passwords.newPassword !== passwords.confirmPassword
            }
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SecurityTab;
